/**
 * Workflow store (Stage 1 — minimal)
 *
 * - Load workflows for the current workspace
 * - Read workflow_id on cycles (via DegapCycle.workflowId) and default_workflow_id on workspace
 * - Resolve effective workflow: cycle.workflow_id → workspace.default_workflow_id → canonical fallback (null)
 *
 * No full workflow management UI yet; create/update/archive provided for when a home exists.
 */

import { create } from 'zustand';
import type { DegapCycle } from '../../types/prioritization/degapCycle';
import type { Workflow, WorkflowConfiguration } from '../../types/prioritization/workflow';
import { getEffectiveWorkflowConfig } from '../../lib/workflowConfig';
import type { EffectiveWorkflowConfig } from '../../types/prioritization/workflow';
import { SESSION_CONTEXT_MODES } from '../../types/prioritization/workflow';
import {
  fetchAll,
  fetchById,
  insertRecord,
  updateRecord,
  getCurrentWorkspaceId,
  getSafeSupabaseClient,
  fromDbFormat,
  isSupabaseAvailable,
  generateUUID,
  isInDemoMode,
} from '../../lib/supabaseSync';
import { useAuditStore } from './auditStore';
import {
  retryWhenWorkspaceContextChanges,
  storeWorkspaceId,
  workspaceSeedBlocker,
} from '../../lib/workspaceSeedGuard';

const TABLE_NAME = 'workflows';

/**
 * Preset workflow policy configurations.
 *
 * These define orchestration policies only (allowed modes, write-back, result
 * resolution, catchball, session defaults). Initialization blueprints (attributes,
 * participants, groups to seed at cycle creation) have moved to CycleTemplate presets
 * in cycleTemplateStore.ts.
 *
 * Used for "Create from preset" in the Workflows settings UI.
 */
export const WORKFLOW_TEMPLATES: Array<{
  name: string;
  description: string;
  configuration: WorkflowConfiguration;
}> = [
  {
    name: 'Global Workflow',
    description: 'Default workflow for the workspace. Allows all session modes; cycles can inherit or override. Use as workspace default or assign to specific cycles.',
    configuration: {
      allowed_session_modes: [...SESSION_CONTEXT_MODES],
      attribute_write_back: 'direct',
      result_write_back: 'to_track',
      governance_write_back: 'proposal_eligible',
      result_resolution: 'latest_published',
      catchball_enabled: true,
      default_timing_mode: 'live',
      default_entry_mode: 'facilitator',
    },
  },
  {
    name: 'Cycle Linked',
    description: 'Full governance workflow: cycle-bound sessions, direct write-back, proposal eligibility. Best for structured, recurring prioritization.',
    configuration: {
      allowed_session_modes: ['cycle_linked'],
      attribute_write_back: 'direct',
      result_write_back: 'to_track',
      governance_write_back: 'proposal_eligible',
      result_resolution: 'latest_published',
      catchball_enabled: true,
      default_timing_mode: 'live',
      default_entry_mode: 'facilitator',
    },
  },
  {
    name: 'App Linked',
    description: 'Lightweight workflow for ad-hoc prioritization. Items come from the app context; results are informational only. No proposals or catchball.',
    configuration: {
      allowed_session_modes: ['app_linked'],
      attribute_write_back: 'session_only',
      result_write_back: 'session_only',
      governance_write_back: 'none',
      result_resolution: 'latest_published',
      catchball_enabled: false,
      default_timing_mode: 'async',
      default_entry_mode: 'participants',
    },
  },
  {
    name: 'Isolated',
    description: 'Standalone sessions with no cycle or track binding. No write-back, no governance. Results stay within the session unless explicitly promoted.',
    configuration: {
      allowed_session_modes: ['isolated'],
      attribute_write_back: 'none',
      result_write_back: 'none',
      governance_write_back: 'none',
      result_resolution: 'latest_published',
      catchball_enabled: false,
      default_timing_mode: 'live',
      default_entry_mode: 'facilitator',
    },
  },
];

export interface WorkflowStoreState {
  workflows: Workflow[];
  /** The workspace `workflows` were read for; null before a read lands (and in demo mode). */
  loadedWorkspaceId: string | null;
  isLoading: boolean;
  loadError: string | null;
  fetchWorkflows: () => Promise<void>;
  /** Returns workspace default_workflow_id or null. */
  getWorkspaceDefaultWorkflowId: (workspaceId: string) => Promise<string | null>;
  /** Resolve effective workflow for a cycle: explicit → workspace default → null (canonical fallback). */
  getEffectiveWorkflowForCycle: (cycle: DegapCycle) => Promise<Workflow | null>;
  /** Resolve effective workflow config for a cycle (for Stage 4 gating). Returns canonical defaults when no workflow. */
  getEffectiveConfigForCycle: (cycle: DegapCycle) => Promise<EffectiveWorkflowConfig>;
  /** Set workspace default workflow. Pass null to clear. */
  setWorkspaceDefaultWorkflow: (workspaceId: string, workflowId: string | null) => Promise<{ success: boolean; error?: string }>;
  createWorkflow: (data: {
    name: string;
    description?: string;
    createdBy: string;
    status?: 'draft' | 'active' | 'archived';
  }) => Promise<Workflow | null>;
  updateWorkflow: (id: string, updates: Partial<Pick<Workflow, 'name' | 'description' | 'status' | 'configuration' | 'templateId'>>) => Promise<{ success: boolean; error?: string }>;
  archiveWorkflow: (id: string) => Promise<{ success: boolean; error?: string }>;
}

// One seeding run per workspace at a time. Concurrent reads that find the same
// workspace empty (the sidebar and the workspace refetch both read on load) wait
// for that run's rows instead of each seeding the full set or showing none.
let _seeding: { workspaceId: string | null; rows: Promise<Workflow[]> } | null = null;
// Each fetch takes a number; only the latest may write the store, so a read
// that started in one workspace cannot land after a read for the next.
let _fetchSeq = 0;

/** Insert the preset workflows into `workspaceId`; make Global Workflow its default if it has none. */
async function seedPresetWorkflows(workspaceId: string | null): Promise<Workflow[]> {
  console.log('[WorkflowStore] No workflows found — seeding preset workflows for session contexts');
  const now = new Date();
  const seeded: Workflow[] = [];
  // Every row names the workspace the empty read ran against, so a set that is
  // finished after a switch still lands where it was found missing.
  for (const seed of WORKFLOW_TEMPLATES) {
    const w: Workflow = {
      id: generateUUID(),
      workspaceId,
      name: seed.name,
      description: seed.description,
      status: 'active',
      configuration: seed.configuration,
      createdAt: now,
      createdBy: 'system',
      updatedAt: now,
    };
    const insertResult = await insertRecord<Workflow>(TABLE_NAME, w);
    seeded.push(insertResult.data ?? w);
  }

  // After seeding, auto-set Global Workflow as the workspace default if none is set yet.
  if (workspaceId) {
    const globalWorkflow = seeded.find((w) => w.name === 'Global Workflow');
    if (globalWorkflow) {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        const { data: wsRow } = await supabase
          .from('workspaces')
          .select('default_workflow_id')
          .eq('id', workspaceId)
          .single();
        if (wsRow && !wsRow.default_workflow_id) {
          await supabase
            .from('workspaces')
            .update({ default_workflow_id: globalWorkflow.id })
            .eq('id', workspaceId);
          console.log('[WorkflowStore] Set Global Workflow as default for workspace', workspaceId);
          try {
            const { useTenantStore } = await import('./tenantStore');
            useTenantStore.getState().updateWorkspace(workspaceId, { defaultWorkflowId: globalWorkflow.id });
          } catch (e) {
            console.warn('[WorkflowStore] Could not sync default workflow to tenant store:', e);
          }
        }
      }
    }
  }
  return seeded;
}

/** A read that found the workspace in flux reads again once it settles. */
function readWorkflowsAgainWhenContextChanges(): void {
  retryWhenWorkspaceContextChanges('workflows', () => {
    void useWorkflowStore.getState().fetchWorkflows();
  });
}

export const useWorkflowStore = create<WorkflowStoreState>()((set, get) => ({
  workflows: [],
  loadedWorkspaceId: null,
  isLoading: false,
  loadError: null,

  fetchWorkflows: async () => {
    if (!isSupabaseAvailable()) {
      set({ loadError: 'Supabase not configured' });
      return;
    }
    const seq = ++_fetchSeq;
    // The workspace this read runs against (null in demo mode, as the rows are written).
    const workspaceId = storeWorkspaceId();
    // Rows held for another workspace are never shown while this one loads.
    set({
      isLoading: true,
      loadError: null,
      ...(get().loadedWorkspaceId !== workspaceId ? { workflows: [], loadedWorkspaceId: null } : {}),
    });
    const result = await fetchAll<Workflow>(TABLE_NAME, { column: 'name', ascending: true }, { filterByWorkspace: true, strictWorkspaceFilter: true });
    if (seq !== _fetchSeq) return; // a newer read owns the store
    if (storeWorkspaceId() !== workspaceId) {
      // The workspace changed during the read: these rows answer for the old one.
      set({ workflows: [], loadedWorkspaceId: null, isLoading: false });
      readWorkflowsAgainWhenContextChanges();
      return;
    }
    if (result.error) {
      set({ isLoading: false, loadError: result.error });
      return;
    }

    let workflows = result.data ?? [];

    if (workflows.length === 0) {
      if (_seeding && _seeding.workspaceId === workspaceId) {
        workflows = await _seeding.rows;
      } else {
        // Only seed into the workspace the empty read ran against, while it is still
        // the one in force, resolved from the server for the person signed in. A null
        // workspace would create workspace_id=null rows the strict filter can't find
        // (unbounded re-seeding); a stale one writes into someone else's workspace.
        const blocker = workspaceSeedBlocker(workspaceId);
        if (blocker) {
          console.log('[WorkflowStore] Not seeding preset workflows:', blocker);
          set({ workflows: [], loadedWorkspaceId: workspaceId, isLoading: false, loadError: null });
          readWorkflowsAgainWhenContextChanges();
          return;
        }
        const run = { workspaceId, rows: seedPresetWorkflows(workspaceId) };
        _seeding = run;
        try {
          workflows = await run.rows;
        } finally {
          if (_seeding === run) _seeding = null;
        }
      }
    }

    // Deduplicate by name — keep the oldest record (lowest createdAt) for each name.
    // This is a safety net for any duplicates that may have been created by prior race
    // conditions; it does not delete them from the DB (use Archive to clean up extras).
    const seenNames = new Map<string, Workflow>();
    for (const wf of workflows) {
      const existing = seenNames.get(wf.name);
      if (!existing) {
        seenNames.set(wf.name, wf);
      } else {
        const existingDate = existing.createdAt instanceof Date ? existing.createdAt : new Date(existing.createdAt as string);
        const wfDate = wf.createdAt instanceof Date ? wf.createdAt : new Date(wf.createdAt as string);
        if (wfDate < existingDate) seenNames.set(wf.name, wf);
      }
    }
    workflows = Array.from(seenNames.values()).sort((a, b) =>
      (a.name ?? '').localeCompare(b.name ?? '')
    );

    if (seq !== _fetchSeq) return;
    if (storeWorkspaceId() !== workspaceId) {
      set({ workflows: [], loadedWorkspaceId: null, isLoading: false });
      readWorkflowsAgainWhenContextChanges();
      return;
    }
    set({ workflows, loadedWorkspaceId: workspaceId, isLoading: false, loadError: null });
  },

  getWorkspaceDefaultWorkflowId: async (workspaceId: string): Promise<string | null> => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return null;
    const { data, error } = await supabase
      .from('workspaces')
      .select('default_workflow_id')
      .eq('id', workspaceId)
      .single();
    if (error || !data) return null;
    const row = fromDbFormat<{ defaultWorkflowId: string | null }>(data as Record<string, unknown>);
    return row.defaultWorkflowId ?? null;
  },

  getEffectiveWorkflowForCycle: async (cycle: DegapCycle): Promise<Workflow | null> => {
    const explicitId = cycle.workflowId ?? null;
    if (explicitId) {
      const result = await fetchById<Workflow>(TABLE_NAME, explicitId);
      return result.data ?? null;
    }
    const workspaceId = cycle.workspaceId ?? getCurrentWorkspaceId();
    if (workspaceId) {
      const defaultId = await get().getWorkspaceDefaultWorkflowId(workspaceId);
      if (defaultId) {
        const result = await fetchById<Workflow>(TABLE_NAME, defaultId);
        return result.data ?? null;
      }
    }
    return null;
  },

  getEffectiveConfigForCycle: async (cycle: DegapCycle): Promise<EffectiveWorkflowConfig> => {
    const workflow = await get().getEffectiveWorkflowForCycle(cycle);
    return getEffectiveWorkflowConfig(workflow);
  },

  setWorkspaceDefaultWorkflow: async (workspaceId: string, workflowId: string | null): Promise<{ success: boolean; error?: string }> => {
    const { checkPolicyForWorkflow } = await import('../../lib/authorization/storeEnforcement');
    const decision = checkPolicyForWorkflow('workflow.set_default', workspaceId, { workspaceId });
    if (!decision.allowed) {
      const reasonCode = decision.reasons[0]?.code || 'DENIED';
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('authorization-denied', {
          detail: { store: 'workflow', action: 'setWorkspaceDefaultWorkflow', message: `Cannot set default workflow: ${reasonCode}`, decision },
        }));
      }
      return { success: false, error: `Cannot set default workflow: ${reasonCode}` };
    }
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'Supabase not configured' };
    const { data, error } = await supabase
      .from('workspaces')
      .update({ default_workflow_id: workflowId })
      .eq('id', workspaceId)
      .select('id')
      .maybeSingle();
    if (error) return { success: false, error: error.message };
    if (!data) return { success: false, error: 'Workspace not found or update not allowed (check RLS). Ensure migration 173 adds default_workflow_id and workspace members can UPDATE workspaces.' };
    // Sync tenant store so workspace switcher and fallback display have correct default (Build Contract: DB wins, cache reflects)
    try {
      const { useTenantStore } = await import('./tenantStore');
      useTenantStore.getState().updateWorkspace(workspaceId, { defaultWorkflowId: workflowId });
    } catch (e) {
      console.warn('[WorkflowStore] Could not sync default to tenant store:', e);
    }
    const auditResult = await useAuditStore.getState().logStage6GovernanceDurable({
      type: 'workspace_default_workflow_changed',
      workspaceId,
      workflowId: workflowId ?? undefined,
      actorId: '',
      actorRole: 'workspace-admin',
      details: { workflowId },
    });
    if (!auditResult.auditSaved && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('store-error', {
        detail: { store: 'workflow', action: 'setDefaultWorkflow', message: `Audit log could not be saved: ${auditResult.error ?? 'unknown'}` },
      }));
    }
    return { success: true };
  },

  createWorkflow: async (data) => {
    if (!isSupabaseAvailable()) return null;
    const workspaceId = isInDemoMode() ? null : getCurrentWorkspaceId();
    if (workspaceId) {
      const { checkPolicyForWorkflow } = await import('../../lib/authorization/storeEnforcement');
      const decision = checkPolicyForWorkflow('workflow.create', workspaceId, { workspaceId });
      if (!decision.allowed) {
        const reasonCode = decision.reasons[0]?.code || 'DENIED';
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('authorization-denied', {
            detail: { store: 'workflow', action: 'createWorkflow', message: `Cannot create workflow: ${reasonCode}`, decision },
          }));
        }
        return null;
      }
    }
    // In demo mode use null workspace_id so RLS allows insert without workspace_memberships.
    const now = new Date();
    const w: Workflow = {
      id: generateUUID(),
      workspaceId,
      name: data.name,
      description: data.description ?? null,
      status: data.status ?? 'active',
      configuration: {},
      createdAt: now,
      createdBy: data.createdBy,
      updatedAt: now,
    };
    const result = await insertRecord<Workflow>(TABLE_NAME, w);
    if (result.error) return null;
    const created = result.data ?? w;
    set((s) => ({ workflows: [...s.workflows, created] }));
    const auditResult = await useAuditStore.getState().logStage6GovernanceDurable({
      type: 'workflow_created',
      workflowId: created.id,
      workspaceId: created.workspaceId ?? getCurrentWorkspaceId() ?? undefined,
      actorId: data.createdBy,
      actorRole: 'workspace-admin',
      details: { name: created.name },
    });
    if (!auditResult.auditSaved && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('store-error', {
        detail: { store: 'workflow', action: 'createWorkflow', message: `Governance event could not be saved: ${auditResult.error ?? 'unknown'}` },
      }));
    }
    return created;
  },

  updateWorkflow: async (id, updates) => {
    if (!isSupabaseAvailable()) return { success: false, error: 'Supabase not configured' };
    const current = get().workflows.find((w) => w.id === id);
    if (!current) return { success: false, error: 'Workflow not found' };
    const isArchiving = updates.status === 'archived';
    if (updates.configuration !== undefined || updates.name !== undefined || updates.description !== undefined || updates.status !== undefined || updates.templateId !== undefined) {
      const workspaceId = current.workspaceId ?? getCurrentWorkspaceId();
      if (workspaceId) {
        const { checkPolicyForWorkflow } = await import('../../lib/authorization/storeEnforcement');
        const action = isArchiving ? 'workflow.archive' : 'workflow.edit';
        const decision = checkPolicyForWorkflow(action, id, { workspaceId });
        if (!decision.allowed) {
          const reasonCode = decision.reasons[0]?.code || 'DENIED';
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'workflow', action: isArchiving ? 'archiveWorkflow' : 'updateWorkflow', message: `Cannot ${isArchiving ? 'archive' : 'edit'} workflow: ${reasonCode}`, decision },
            }));
          }
          return { success: false, error: `Cannot ${isArchiving ? 'archive' : 'edit'} workflow: ${reasonCode}` };
        }
      }
    }
    const result = await updateRecord<Workflow>(TABLE_NAME, id, { ...current, ...updates, updatedAt: new Date() });
    if (result.error) return { success: false, error: result.error };
    if (result.data) {
      set((s) => ({
        workflows: s.workflows.map((w) => (w.id === id ? (result.data as Workflow) : w)),
      }));
      const touched = updates.configuration !== undefined || updates.name !== undefined || updates.description !== undefined || updates.status !== undefined;
      if (touched) {
        // governance_events requires workspace_id for Stage6 workflow events; use current workspace when workflow row has none
        const workspaceIdForAudit = current.workspaceId ?? getCurrentWorkspaceId() ?? undefined;
        const auditResult = await useAuditStore.getState().logStage6GovernanceDurable(
          updates.status === 'archived'
            ? { type: 'workflow_archived', workflowId: id, workspaceId: workspaceIdForAudit, actorId: '', actorRole: 'workspace-admin', details: {} }
            : { type: 'workflow_config_changed', workflowId: id, workspaceId: workspaceIdForAudit, actorId: '', actorRole: 'workspace-admin', details: { ...updates } }
        );
        if (!auditResult.auditSaved && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('store-error', {
            detail: { store: 'workflow', action: 'updateWorkflow', message: `Governance event could not be saved: ${auditResult.error ?? 'unknown'}` },
          }));
        }
      }
    }
    return { success: true };
  },

  archiveWorkflow: async (id) => {
    const current = get().workflows.find((w) => w.id === id);
    if (!current) return { success: false, error: 'Workflow not found' };
    const workspaceId = current.workspaceId ?? getCurrentWorkspaceId();
    if (workspaceId) {
      const { checkPolicyForWorkflow } = await import('../../lib/authorization/storeEnforcement');
      const decision = checkPolicyForWorkflow('workflow.archive', id, { workspaceId });
      if (!decision.allowed) {
        const reasonCode = decision.reasons[0]?.code || 'DENIED';
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('authorization-denied', {
            detail: { store: 'workflow', action: 'archiveWorkflow', message: `Cannot archive workflow: ${reasonCode}`, decision },
          }));
        }
        return { success: false, error: `Cannot archive workflow: ${reasonCode}` };
      }
    }
    return get().updateWorkflow(id, { status: 'archived' });
  },
}));
